import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, ChevronDown, ExternalLink } from 'lucide-react';
import { Header } from './Header';
import { Footer } from './Footer';
import { SEOHead } from './SEOHead';
import { Hero } from './Hero';
import { ProductShowcase } from './ProductShowcase';
import { ContactSection } from './ContactSection';
import { RedeemRelicModal } from './RedeemRelicModal';
import { useContentData } from '../hooks/useContentData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RelicsPage = () => {
  const { data } = useContentData();
  const [heroData, setHeroData] = useState(null);
  const [cardsData, setCardsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  
  // For product showcase
  const firstProduct = data.products?.[0];
  const [selectedVariant, setSelectedVariant] = useState(null);
  
  React.useEffect(() => {
    if (firstProduct && !selectedVariant) {
      setSelectedVariant(firstProduct.variants?.[0]);
    }
  }, [firstProduct, selectedVariant]);

  useEffect(() => {
    fetchRelicsContent();
  }, []);

  const fetchRelicsContent = async () => {
    try {
      const [heroRes, cardsRes] = await Promise.all([
        axios.get(`${API_URL}/api/relics/hero`),
        axios.get(`${API_URL}/api/relics/cards`)
      ]);
      
      setHeroData(heroRes.data);
      setCardsData(cardsRes.data);
    } catch (error) {
      console.error('Error fetching relics content:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead seoData={data.seo} productData={data.products} settings={data.settings} />
      <Header topnavData={data.topnav} settings={data.settings} />
      
      {/* Hero Section - Reuse existing Hero component */}
      {heroData && (
        <Hero 
          heroData={{
            badge_text: heroData.season_badge,
            title: heroData.title_line_1 + ' ' + heroData.title_line_2,
            title_line_1: heroData.title_line_1,
            title_line_2: heroData.title_line_2,
            subtitle: heroData.subheading,
            description: heroData.description,
            cta_text: heroData.primary_button_text,
            cta_url: '#redeem',
            onRedeemClick: () => setShowRedeemModal(true),
            secondary_cta_text: heroData.secondary_button_text,
            secondary_cta_url: heroData.secondary_button_url,
            product_box_image_url: heroData.hero_image_url,
            hero_background_image_url: heroData.bg_image_url,
            badges: []
          }} 
          settings={data.settings}
        />
      )}
      
      {/* Redeem Modal */}
      <RedeemRelicModal 
        isOpen={showRedeemModal}
        onClose={() => setShowRedeemModal(false)}
      />
      
      {/* Current Collection */}
      <RelicsCollection 
        cardsData={cardsData} 
        heroData={heroData}
        onRedeemClick={() => setShowRedeemModal(true)}
      />
      
      {/* Product Showcase */}
      {firstProduct && (
        <ProductShowcase 
          product={firstProduct}
          selectedVariant={selectedVariant} 
          setSelectedVariant={setSelectedVariant}
          hideHeader={true}
        />
      )}
      
      <Footer footerData={data.footer} settings={data.settings} />
    </div>
  );
};

// Collection Component with Horizontal Scroll
const RelicsCollection = ({ cardsData, heroData, onRedeemClick }) => {
  const [categoryPages, setCategoryPages] = useState({});
  const [flippedCards, setFlippedCards] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  
  // Check if there are multiple seasons
  const allSeasons = new Set();
  cardsData.forEach(group => {
    group.cards.forEach(card => allSeasons.add(card.season));
  });
  const showSeasonLabels = allSeasons.size > 1;

  // Initialize page state for each category
  React.useEffect(() => {
    const pages = {};
    cardsData.forEach(group => {
      pages[group.category.id] = 0;
    });
    setCategoryPages(pages);
  }, [cardsData]);

  // Detect mobile/desktop
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const scrollCategory = (categoryId, direction) => {
    setCategoryPages(prev => {
      const category = cardsData.find(g => g.category.id === categoryId);
      if (!category) return prev;
      
      const totalPages = Math.ceil(category.cards.length / 6);
      const currentPage = prev[categoryId] || 0;
      let newPage = currentPage;
      
      if (direction === 'left') {
        newPage = Math.max(0, currentPage - 1);
      } else {
        newPage = Math.min(totalPages - 1, currentPage + 1);
      }
      
      return { ...prev, [categoryId]: newPage };
    });
  };

  const getVisibleCards = (cards, categoryId) => {
    const page = categoryPages[categoryId] || 0;
    const start = page * 6;
    const end = start + 6;
    return cards.slice(start, end);
  };

  const getCardsToDisplay = (cards, categoryId) => {
    return isMobile ? cards : getVisibleCards(cards, categoryId);
  };

  const toggleCardFlip = (cardId) => {
    setFlippedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  return (
    <section id="season_content" className="py-12 md:py-20 bg-gray-50" style={{ paddingBottom: '1rem' }}>
      <style>{`
        /* Card flip animations */
        .card-flip-container {
          width: 100%;
          perspective: 1000px;
          cursor: pointer;
        }
        .card-flip-inner {
          position: relative;
          width: 100%;
          transition: transform 0.7s;
          transform-style: preserve-3d;
        }
        /* Desktop hover behavior - ONLY on non-touch devices */
        @media (min-width: 768px) and (hover: hover) {
          .card-flip-container:hover .card-flip-inner:not(.flipped) {
            transform: rotateY(180deg);
          }
        }
        /* Mobile/Touch toggle behavior - controlled by state */
        .card-flip-inner.flipped {
          transform: rotateY(180deg);
        }
        .card-flip-front,
        .card-flip-back {
          width: 100%;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }
        .card-flip-back {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          transform: rotateY(180deg);
        }
        
        /* Cards container - Different behavior for mobile/desktop */
        .cards-container {
          display: flex;
        }
        
        /* Mobile: Horizontal scroll */
        @media (max-width: 767px) {
          .cards-container {
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            -ms-overflow-style: none;
            padding: 0 1rem;
            margin: 0 -1rem;
          }
          .cards-container::-webkit-scrollbar {
            display: none;
          }
          .card-item {
            scroll-snap-align: start;
            flex-shrink: 0;
          }
        }
        
        /* Desktop: Centered flex */
        @media (min-width: 768px) {
          .cards-container {
            justify-content: center;
            flex-wrap: nowrap;
          }
          .card-item {
            flex-shrink: 0;
          }
        }
      `}</style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-12">
          <h2 className="gaming-title text-3xl md:text-4xl lg:text-5xl font-bold mb-2 uppercase">
            <span className="text-orange-500">CURRENT</span>{' '}
            <span className="text-gray-900">COLLECTION</span>
          </h2>
          <div className="flex items-center justify-center space-x-2 mt-3 md:mt-4">
            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
            <span className="gaming-text text-orange-500 uppercase tracking-widest text-xs md:text-sm">Season 1</span>
          </div>
        </div>

        {/* Categories */}
        <div className="space-y-12 md:space-y-16">
          {cardsData.map((group) => (
            <div key={group.category.id} className="relative">
              {/* Category Title with decorative line */}
              <div className="text-center mb-6 md:mb-8" style={{ marginBottom: '1rem' }}>
                <div className="flex items-center justify-center space-x-4">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-orange-500 to-transparent max-w-md"></div>
                  <h3 className="gaming-title text-lg md:text-xl lg:text-2xl font-bold text-gray-900 uppercase px-4">
                    {group.category.name}
                  </h3>
                  <div className="h-px flex-1 bg-gradient-to-l from-transparent via-orange-500 to-transparent max-w-md"></div>
                </div>
              </div>

              {/* Cards Carousel - Single HTML, Different CSS for Mobile/Desktop */}
              <div className="relative">
                <div className="cards-container gap-4">
                  {getCardsToDisplay(group.cards, group.category.id).map((card) => (
                    <div
                      key={card.id}
                      className="w-56 relative card-item"
                    >
                      {card.back_image_url ? (
                        <div 
                          className="card-flip-container"
                        >
                          <div className={`card-flip-inner ${flippedCards[card.id] ? 'flipped' : ''}`}>
                            {/* Front of card */}
                            <div className="card-flip-front" onClick={() => toggleCardFlip(card.id)}>
                              <div className="aspect-[3/4] bg-gray-900 rounded-lg overflow-hidden shadow-xl border border-gray-700">
                                <img
                                  src={card.image_url}
                                  alt={card.alt_text}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            </div>
                            
                            {/* Back of card */}
                            <div className="card-flip-back" onClick={() => toggleCardFlip(card.id)}>
                              <div className="aspect-[3/4] bg-gray-900 rounded-lg overflow-hidden shadow-xl border border-gray-700">
                                <img
                                  src={card.back_image_url}
                                  alt={`${card.alt_text} - back`}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="aspect-[3/4] bg-gray-900 rounded-lg overflow-hidden shadow-xl hover:shadow-2xl transition-all border border-gray-700">
                          <img
                            src={card.image_url}
                            alt={card.alt_text}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      {showSeasonLabels && (
                        <p className="text-center text-xs text-gray-500 mt-2">Season {card.season}</p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Pagination Arrows at Bottom - Desktop Only */}
                {group.cards.length > 6 && (
                  <div className="hidden md:flex items-center justify-center space-x-4 mt-6">
                    <button
                      onClick={() => scrollCategory(group.category.id, 'left')}
                      disabled={(categoryPages[group.category.id] || 0) === 0}
                      className="w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <span className="text-sm text-gray-600">
                      {(categoryPages[group.category.id] || 0) + 1} / {Math.ceil(group.cards.length / 6)}
                    </span>
                    <button
                      onClick={() => scrollCategory(group.category.id, 'right')}
                      disabled={(categoryPages[group.category.id] || 0) >= Math.ceil(group.cards.length / 6) - 1}
                      className="w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Redeem CTA - Inside the gray section */}
        {heroData && (
          <div className="text-center mt-12 md:mt-20 px-4" style={{ marginBottom: '3rem' }}>
            {/* Season Badge */}
            {heroData.season_badge && (
              <div className="inline-flex items-center space-x-2 bg-orange-50 px-4 md:px-6 py-2 rounded-full border border-orange-500/50 mb-4 md:mb-6">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span className="gaming-text font-bold text-orange-500 uppercase tracking-widest text-xs md:text-sm">
                  {heroData.season_badge}
                </span>
              </div>
            )}

            <h2 className="gaming-title text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4 uppercase">
              Redeem A Relic
            </h2>
            
            <p className="text-gray-600 text-base md:text-lg mb-6 md:mb-8">
              Click on the redeem a relic and get yours now
            </p>

            <div className="flex flex-col items-stretch justify-center space-y-3 md:space-y-0 md:flex-row md:items-center md:space-x-4 max-w-md mx-auto md:max-w-none">
              <button
                onClick={onRedeemClick}
                className="gaming-title w-full md:w-auto px-6 md:px-8 py-3 md:py-4 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all duration-300 shadow-lg uppercase font-bold text-sm md:text-base"
              >
                {heroData.primary_button_text}
              </button>
              <a
                href={heroData.secondary_button_url}
                target="_blank"
                rel="noopener noreferrer"
                className="gaming-title w-full md:w-auto px-6 md:px-8 py-3 md:py-4 border-2 border-orange-500 text-orange-500 rounded-lg hover:bg-orange-500 hover:text-white transition-all duration-300 uppercase font-bold text-sm md:text-base inline-block text-center"
              >
                {heroData.secondary_button_text}
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
